// steps/UserSigninVerifyOtpStep.tsx

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import ErrorMessage from '@/components/common/ErrorMessage';
import CustomOtpInput from '@/components/form/CustomOtpInput';

import { useApp } from '@/contexts/AppContext';

import { BasicResponse } from '@/core/models/common-types';
import { OtpChannel } from '@/core/enums/otp-channel.enum';
import { extractErrorMessage } from '@/core/utils/utils';

import { userSigninVerifyOtpSchema, UserSigninVerifyOtpFormValues } from '../schemas/userSigninVerifyOtpSchema';

import { UserSigninAction, UserSigninState } from '../states/userSigninReducer';
import axios from 'axios';

interface UserSigninVerifyOtpStepProps {
  onSubmit: (data: UserSigninVerifyOtpFormValues) => void;
  onPrevious: () => void;
  state: UserSigninState;
  dispatch: React.Dispatch<UserSigninAction>;
}

const UserSigninVerifyOtpStep: React.FC<UserSigninVerifyOtpStepProps> = ({  onPrevious, state }) => {
  const { isLoading, baseUrl } = useApp();

  const [error, setError] = useState<BasicResponse | null>(null);

  const { control, handleSubmit } = useForm<UserSigninVerifyOtpFormValues>({
    resolver: zodResolver(userSigninVerifyOtpSchema),
    defaultValues: {
      otpCode: state.otpCode ?? '',
    },
  });
  const submitForm = async (formValues: UserSigninVerifyOtpFormValues) => {
    try {
      setError(null);

      const { data } = await axios.post(`${baseUrl}/api/v2/UserSignin/VerifyOtp`, {
        loginSessionId: state.loginSessionId,
        otpCode: formValues.otpCode,
      });
      window.location.href = data.redirectUrl;
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const resendOtp = async () => {
    try {
      setError(null);
      await axios.post(`${baseUrl}/api/v2/UserSignin/ResendOtp`, {
        loginSessionId: state.loginSessionId,
      });
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };
  const recipient = state.otpChannel === OtpChannel.Sms ? state.phoneNumber : state.email;

  return (
    <>
      <ErrorMessage error={error} />

      <form onSubmit={handleSubmit(submitForm)} noValidate>
        <div className="mb-4">
          <h5 className="fw-semibold">Verify OTP</h5>

          <p className="text-muted mb-1">Enter the verification code sent to:</p>

          <p className="fw-semibold mb-0">{recipient}</p>
        </div>

        <CustomOtpInput control={control} name="otpCode" required />

        <div className="text-center mt-3">
          <button type="button" className="btn btn-link text-decoration-none" onClick={resendOtp} disabled={isLoading}>
            Resend OTP
          </button>
        </div>

        <div className="d-flex justify-content-between mt-4">
          <button type="button" className="btn btn-outline-secondary" onClick={onPrevious} disabled={isLoading}>
            Back
          </button>

          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Verifying...' : 'Verify'}
          </button>
        </div>
      </form>
    </>
  );
};

export default UserSigninVerifyOtpStep;
